import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Slot } from 'nano-slots';
import { useTranslation } from 'react-i18next';
import Dialog from 'Components/Molecules/Dialog/Dialog';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { ErrorBoundary } from 'Components/Organisms/DrawerStackProvider/DrawerStack';
import ModalStackItemContext from './ModalStackItemContext';

// The same transition between pages as the drawers.
const variants = {
    incoming: (direction) => ({
        x: direction > 0 ? 100 : -100,
        opacity: 0,
        position: 'absolute',
        inset: 0,
    }),
    active: { x: 0, opacity: 1, position: 'relative' },
    exit: (direction) => ({
        x: direction > 0 ? -100 : 100,
        opacity: 0.2,
        display: 'none',
        position: 'absolute',
        inset: 0,
    }),
};

const transition = { duration: 0.15 };

export default function ModalStack({ modals, modalTypes, direction, onBack, onClose }) {
    const { t } = useTranslation('common');
    const { showUnknownError } = useErrors();

    const index = modals.length - 1;
    const current = modals[index];
    const options = { ...modalTypes[current?.id]?.modalStackProps, ...current?.props.modalStackProps };

    return (
        <Dialog
            open={modals.length > 0}
            onClose={onClose}
            onBack={index > 0 ? onBack : undefined}
            backLabel={t('back')}
            size={options.size ?? 'md'}
            title={
                <span className="flex items-center gap-2">
                    {current?.props.title}
                    <Slot name="modal-stack-title" />
                </span>
            }
        >
            <div className="relative">
                {modals.map((modal, i) => {
                    const Component = modalTypes[modal.id];

                    if (!Component) return null;

                    return (
                        <motion.div
                            // Several modals of the same type can be stacked.
                            key={`${modal.id}-${i}`}
                            custom={direction}
                            variants={variants}
                            transition={transition}
                            initial="incoming"
                            animate={i === index ? 'active' : 'exit'}
                        >
                            <ErrorBoundary
                                onError={(error) => {
                                    showUnknownError(error);
                                    onClose();
                                }}
                            >
                                <ModalStackItemContext.Provider value={{ isActive: i === index }}>
                                    <Component {...modal.props} onBack={onBack} onClose={onClose} />
                                </ModalStackItemContext.Provider>
                            </ErrorBoundary>
                        </motion.div>
                    );
                })}
            </div>
        </Dialog>
    );
}

ModalStack.propTypes = {
    modals: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            props: PropTypes.object.isRequired,
        }),
    ).isRequired,
    modalTypes: PropTypes.objectOf(PropTypes.elementType).isRequired,
    direction: PropTypes.number.isRequired,
    onBack: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
